/**
 * BodySilhouetteAnatomical — Configurable minimalist body silhouette used by
 * InteractiveBodyMap. Super Admin can choose the global style via Platform
 * Settings → body_diagram_style. Every style supports male + female.
 *
 * Available styles (must match backend ALLOWED_BODY_DIAGRAM_STYLES):
 *   • apple_minimal   — Apple Health-inspired soft grey silhouette (default)
 *   • anatomical      — Warmer skin-tone fill with subtle contour lines
 *   • outline         — Clean line-art only, no fill (WHOOP-style)
 *   • muscle_group    — Fitness-style silhouette with muscle-group tints
 *   • gradient_glow   — Bold aurora gradient fill for medical dashboards
 *   • wireframe       — Blueprint/CAD-style dashed silhouette
 *
 * viewBox is 200×500. The parent controls actual size via aspect-ratio.
 */
import React from "react";

// Base silhouette paths — reused by every style so hotspot alignment stays constant.
const MALE_PATH = `
  M100,32 a24,26 0 1 1 -0.1 0 Z
  M85,60 L115,60 L124,84
  Q160,90 172,140 Q180,180 168,220
  Q158,254 150,290 L150,308
  Q152,340 150,380 L145,478
  Q145,486 137,486 L118,486
  Q112,486 112,478 L108,382
  Q105,346 105,314 L95,314
  Q95,346 92,382 L88,478
  Q88,486 82,486 L63,486
  Q55,486 55,478 L50,380
  Q48,340 50,308 L50,290
  Q42,254 32,220 Q20,180 28,140
  Q40,90 76,84 Z`;

const FEMALE_PATH = `
  M100,30 a22,24 0 1 1 -0.1 0 Z
  M88,58 L112,58 L118,80
  Q152,86 158,132 Q158,150 148,168
  Q145,180 148,192 Q158,208 158,224
  Q160,254 152,286 L154,306
  Q158,336 158,376 L152,478
  Q152,486 144,486 L124,486
  Q118,486 118,478 L112,382
  Q108,352 108,320 L92,320
  Q92,352 88,382 L82,478
  Q82,486 76,486 L56,486
  Q48,486 48,478 L42,376
  Q42,336 46,306 L48,286
  Q40,254 42,224 Q42,208 52,192
  Q55,180 52,168 Q42,150 42,132
  Q48,86 82,80 Z`;

// Palette per style — each has { fill (gradient stops), stroke, halo, bg }
const STYLE_PRESETS = {
  apple_minimal: {
    stops: ["#F5F7FA", "#E4E9F0", "#D1D8E3"],
    stroke: "#94A3B8",
    strokeWidth: 0.75,
    halo: "rgba(148,163,184,0.14)",
    dash: null,
    extraGuides: false,
  },
  anatomical: {
    stops: ["#FDE7D6", "#F6C9A6", "#E5A379"],
    stroke: "#9C5A2B",
    strokeWidth: 0.9,
    halo: "rgba(230,150,100,0.18)",
    dash: null,
    extraGuides: true,
  },
  outline: {
    stops: ["#FFFFFF", "#FFFFFF", "#FFFFFF"],
    stroke: "#0F172A",
    strokeWidth: 1.6,
    halo: "rgba(15,23,42,0.06)",
    dash: null,
    extraGuides: false,
  },
  muscle_group: {
    stops: ["#E9F5FF", "#BFE0FA", "#8AC3F0"],
    stroke: "#1E3A8A",
    strokeWidth: 0.9,
    halo: "rgba(59,130,246,0.16)",
    dash: null,
    extraGuides: "muscle",
  },
  gradient_glow: {
    stops: ["#A78BFA", "#7C3AED", "#4C1D95"],
    stroke: "#EDE9FE",
    strokeWidth: 0.75,
    halo: "rgba(167,139,250,0.30)",
    dash: null,
    extraGuides: false,
    dark: true,
  },
  wireframe: {
    stops: ["rgba(59,130,246,0.05)", "rgba(59,130,246,0.05)", "rgba(59,130,246,0.05)"],
    stroke: "#2563EB",
    strokeWidth: 1.1,
    halo: "rgba(37,99,235,0.10)",
    dash: "4 3",
    extraGuides: "wireframe",
  },
};

export function BodySilhouetteAnatomical({ gender, style = "apple_minimal" }) {
  const isFemale = gender === "female";
  const path = isFemale ? FEMALE_PATH : MALE_PATH;
  const preset = STYLE_PRESETS[style] || STYLE_PRESETS.apple_minimal;
  const gid = `bm-fill-${style}`;
  const hid = `bm-halo-${style}`;

  return (
    <svg
      viewBox="0 0 200 500"
      xmlns="http://www.w3.org/2000/svg"
      preserveAspectRatio="xMidYMid meet"
      style={{ position: "absolute", inset: 0, width: "100%", height: "100%", pointerEvents: "none" }}
      aria-hidden="true"
      data-body-style={style}
    >
      <defs>
        <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={preset.stops[0]} />
          <stop offset="60%" stopColor={preset.stops[1]} />
          <stop offset="100%" stopColor={preset.stops[2]} />
        </linearGradient>
        <radialGradient id={hid} cx="50%" cy="35%" r="70%">
          <stop offset="0%" stopColor={preset.halo} />
          <stop offset="100%" stopColor="rgba(148,163,184,0)" />
        </radialGradient>
      </defs>

      {/* Halo */}
      <ellipse cx="100" cy="250" rx="120" ry="230" fill={`url(#${hid})`} />

      {/* Silhouette */}
      <path
        d={path}
        fill={`url(#${gid})`}
        stroke={preset.stroke}
        strokeWidth={preset.strokeWidth}
        strokeLinejoin="round"
        strokeDasharray={preset.dash || undefined}
      />

      {/* Optional guide overlays */}
      {preset.extraGuides === "muscle" && (
        <g opacity="0.5" fill="none" stroke={preset.stroke} strokeWidth="0.6">
          {/* Faint pectoral / abdominal / quad guides */}
          <path d="M78,110 Q100,118 122,110" />
          <path d="M100,110 L100,180" />
          <path d="M85,150 L115,150" />
          <path d="M85,170 L115,170" />
          <path d="M75,330 L95,330 M105,330 L125,330" />
          <path d="M75,400 L95,400 M105,400 L125,400" />
        </g>
      )}
      {preset.extraGuides === "wireframe" && (
        <g opacity="0.55" fill="none" stroke={preset.stroke} strokeWidth="0.5" strokeDasharray="2 2">
          <line x1="10" y1="60" x2="190" y2="60" />
          <line x1="10" y1="140" x2="190" y2="140" />
          <line x1="10" y1="240" x2="190" y2="240" />
          <line x1="10" y1="340" x2="190" y2="340" />
          <line x1="100" y1="6" x2="100" y2="494" />
        </g>
      )}
      {preset.extraGuides === true && (
        <g opacity="0.35" fill="none" stroke={preset.stroke} strokeWidth="0.55">
          <path d="M100,60 L100,290" />
          <path d="M60,180 Q100,190 140,180" />
        </g>
      )}
    </svg>
  );
}

// Metadata exposed to the Super Admin selector.
export const BODY_DIAGRAM_STYLES = [
  { id: "apple_minimal", label: "Apple Minimal", hint: "Soft warm-grey silhouette (default)" },
  { id: "anatomical", label: "Anatomical Skin", hint: "Warm skin-tone with contour guides" },
  { id: "outline", label: "Outline", hint: "Clean line-art only, no fill" },
  { id: "muscle_group", label: "Muscle Group", hint: "Fitness-style with muscle guides" },
  { id: "gradient_glow", label: "Gradient Glow", hint: "Bold purple aurora fill" },
  { id: "wireframe", label: "Wireframe", hint: "Blueprint / CAD dashed silhouette" },
];

export default BodySilhouetteAnatomical;
